import {
  Clock, GitBranch, Gauge, LayoutGrid, Phone, Settings, Users, Voicemail,
} from 'lucide-react';
import { ROLES } from './session.jsx';

/* One source of truth for the sidebar, the routes, and the topbar title.

   `roles` is what an access level may see. Sections an extension user has no
   business in — the whole-tenant ones — simply are not in their nav. The
   backend enforces the same boundary; this list only keeps the UI honest. */

const BOTH = [ROLES.TENANT_ADMIN, ROLES.EXTENSION_USER];
const ADMIN_ONLY = [ROLES.TENANT_ADMIN];

export const NAV = [
  { id: 'overview', path: '/', label: 'Overview', icon: LayoutGrid, roles: BOTH },
  { id: 'history', path: '/call-history', label: 'Call history', icon: Clock, roles: BOTH },
  { id: 'voicemail', path: '/voicemail', label: 'Voicemail', icon: Voicemail, roles: BOTH },
  {
    id: 'extensions',
    path: '/extensions',
    label: 'Extensions',
    // An extension user has exactly one, so the plural would be a lie.
    labelFor: { [ROLES.EXTENSION_USER]: 'My extension' },
    icon: Phone,
    roles: BOTH,
  },
  {
    id: 'minutes',
    path: '/minutes',
    label: 'Allocated minutes',
    // An extension user sees only their own line, so the plural whole-account
    // framing would promise more than the page shows them.
    labelFor: { [ROLES.EXTENSION_USER]: 'My minutes' },
    icon: Gauge,
    roles: BOTH,
    // Kept out of the sidebar while the allocation model settles, but still
    // routable: /minutes renders as it always did. Delete this line to show it.
    hidden: true,
  },
  { id: 'queues', path: '/queues', label: 'Queues & groups', icon: Users, roles: ADMIN_ONLY },
  { id: 'routing', path: '/call-routing', label: 'Call routing', icon: GitBranch, roles: ADMIN_ONLY },
  { id: 'hours', path: '/business-hours', label: 'Business hours', icon: Clock, roles: ADMIN_ONLY },
  { id: 'settings', path: '/settings', label: 'Settings', icon: Settings, roles: BOTH },
];

/* Every section this role may reach, whether or not the sidebar lists it.

   Separate from navFor because "don't advertise this yet" and "this page does
   not exist" are different things: a hidden section still has to route, or
   opening its URL would 404 a page that works perfectly well. */
export function routesFor(role) {
  return NAV.filter((n) => n.roles.includes(role)).map((n) => ({
    ...n,
    label: n.labelFor?.[role] ?? n.label,
  }));
}

/** What the sidebar renders — routable sections minus the hidden ones. */
export function navFor(role) {
  return routesFor(role).filter((n) => !n.hidden);
}

export function matchRoute(path, role) {
  const items = routesFor(role);

  const exact = items.find((n) => n.path === path);
  if (exact) return exact;

  // /extensions/1003 — an admin drilling into one extension. It keeps the
  // Extensions nav item lit rather than looking like a page of its own.
  const drill = /^\/extensions\/(\d{2,8})$/.exec(path);
  if (drill) {
    const parent = items.find((n) => n.id === 'extensions');
    if (parent) {
      return { ...parent, id: 'extension-detail', params: { extension: drill[1] } };
    }
  }

  return null;
}
